// reference: https://stackoverflow.com/a/19709846
const httpUrlRegex = /^(?:[a-z]+:)?\/\//i; // https://regex101.com/r/1txO91/1
const pathDotRegex = /^\.+$/;
const staticAssetExtensionRegex = /\.[0-9a-zA-Z]+$/;
const staticAPIRoutePath = 'api/static';

export const openSourceAssetBasePath = process.env.openSourceContentLocalDir;
export const internalSourceAssetBasePath = process.env.contentLocalDir;

export const showYouAreLeavingRobloxWarning = (url: string): boolean => {
  // relative urls are roblox urls that are probably loading assets

  if (url.startsWith('tel:') || url.startsWith('mailto:')) {
    return true;
  }
  if (httpUrlRegex.test(url)) {
    try {
      // check the end of hostnames
      const urlObj = new URL(url);
      const urlProtocol = urlObj.protocol.toLowerCase();
      const urlHostname = urlObj.hostname.toLowerCase();
      const isRobloxLink =
        urlHostname.endsWith('.roblox.com') ||
        urlHostname.endsWith('.rbx.com') ||
        urlHostname === 'luau-lang.org' ||
        urlHostname === 'roblox.com' ||
        urlProtocol === 'roblox:';
      // if not RobloxLink, return true
      return !isRobloxLink;
    } catch {
      return true;
    }
  }
  // if it's not http, it's probably an asset or page link
  return false;
};

// remove all the relative parts (e.g. '../') from the path
export const removeRelativePaths = (path: string) => {
  return path
    .split('/')
    .filter((pathPart) => pathPart.length !== 0 && !pathDotRegex.test(pathPart))
    .join('/');
};

/**
 * Get the URL for a link to an asset
 */
export const getUrlForAssetLink = ({
  srcOrHref,
  useSiteBasePath,
}: {
  srcOrHref: string;
  useSiteBasePath: boolean;
}): string => {
  const relativePath = removeRelativePaths(srcOrHref);

  if (relativePath)
    if (process.env.connectsRemoteServer || process.env.assetsBasePath) {
      const sourceBasePath = process.env.assetsBasePath || process.env.contentBasePath;
      if (typeof sourceBasePath === 'undefined') {
        throw new Error(
          'The source base path is not defined during build! Please specify it through the environment variable!',
        );
      }
      // in production, the paths are URLs that the URL constructor resolves for us
      try {
        const newUrl = new URL(relativePath, sourceBasePath).toString();
        return newUrl;
      } catch (e) {
        if (process.env.isExport) {
          return '';
        }
        throw e;
      }
    }

  // otherwise, use the static API route
  return `${useSiteBasePath ? process.env.siteBasePath : ''}/${staticAPIRoutePath}/${relativePath}`;
};

export const resolveStaticSrc = (src: string | undefined): string | undefined => {
  if (typeof src === 'undefined') {
    return src;
  }

  // do not do anything with external URLs
  if (httpUrlRegex.test(src)) {
    return src;
  }

  return getUrlForAssetLink({ srcOrHref: src, useSiteBasePath: true });
};

export const resolveStaticHref = (href: string | undefined): string => {
  if (typeof href === 'undefined') {
    return '';
  }

  // don't do anything with external URLs
  if (httpUrlRegex.test(href)) {
    return href;
  }

  // if the link has an asset extension, process it
  if (staticAssetExtensionRegex.test(href)) {
    return getUrlForAssetLink({ srcOrHref: href, useSiteBasePath: false });
  }

  // if the link doesn't have a file extension, return it as is
  return href;
};
